import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import SelenaA from "../../../../assets/img/service/selena-a.webp";

import {
  Smoking,
  SonicFacilityData,
} from "../../../../common/classes/SonicFacility";
import { ImageSourceSetData } from "../../../../common/classes/ImageSourceSet";
import BackBar from "../../../../common/nav/BackBar";
import SaveButton from "../../../../common/nav/SaveButton";
import Form from "../../../../common/forms/Form";
import FormInput from "../../../../common/forms/FormInput";
import FormMultipleImages from "../../../../common/forms/FormMultipleImages";
import LoadingSpinner from "../../../../common/LoadingSpinner";
import NotificationModal from "../../../../common/settings-modal/NotificationModal";

import { ValType } from "../../../../utils/ValidationFunctions";
import {
  RequestState,
  genNotificationColor,
  genNotificationIcon,
  genNotificationMessage,
} from "../../../../utils/RequestState";

import styles from "../EditBase.module.scss";
import ChannelURLHelper from "../database_entries/ChannelURLHelper";
import CheckInputArea from "../../../../common/forms/CheckInputArea";
import {
  facilityTraitOptions,
  facilityTypeOptions,
} from "../../../../utils/FacilityState";

type Props = {
  facility?: SonicFacilityData;
  imgItems: Array<File | ImageSourceSetData>;
  traits: Array<string>;
  types: Array<string>;
  requestState: RequestState;
  isHasChanges: boolean;
  error: boolean;
  handleInputChange: (value: File | string | undefined, name: string) => void;
  deleteImgItem: (img: File | ImageSourceSetData, i: number) => void;
  handleTraits: (value: string) => void;
  handleTypes: (value: string) => void;
  handleSubmit?: () => void;
  handleSave?: () => void;
};

const FacilityForm: React.FC<Props> = ({
  facility,
  imgItems,
  traits,
  types,
  requestState,
  isHasChanges,
  error,
  handleInputChange,
  deleteImgItem,
  handleTraits,
  handleTypes,
  handleSubmit,
  handleSave,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (requestState === RequestState.Doing) {
      window.scrollTo(0, 0);
    }
  }, [requestState]);

  return (
    <>
      {requestState !== RequestState.Doing ? (
        <>
          <BackBar
            title={facility ? "施設編集" : "施設作成"}
            editable={true}
            handleClose={() => navigate(-1)}
            submitHandler={handleSubmit}
            saveHandler={handleSave}
            hideSubmitButton={
              (!facility && !isHasChanges) || (facility && isHasChanges)
                ? true
                : false
            }
            hideSaveButton={!isHasChanges ? true : false}
          />
          <Form handler={() => {}} className={styles["p-edit__wrapper"]}>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="text"
                label="施設名"
                label_class={`${styles["p-edit__form-title"]} ${styles["required-mark"]}`}
                name="sonic_name"
                style_class={styles["p-edit__form-input"]}
                handler={handleInputChange}
                val_type={ValType.NotNull}
                initial_value={facility?.name}
                placeholder="eスポーツカフェ SELeCT渋谷"
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="text"
                label="施設名ふりがな"
                label_class={styles["p-edit__form-title"]}
                name="sonic_kana"
                style_class={styles["p-edit__form-input"]}
                handler={handleInputChange}
                initial_value={facility?.kana}
                placeholder="いーすぽーつかふぇせれくとしぶや"
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="prefecture"
                label="都道府県"
                label_class={styles["p-edit__form-title"]}
                name="sonic_prefecture"
                select_class={styles["p-edit__form-input"]}
                holder_class={styles["p-edit__form-input-novalue"]}
                handler={handleInputChange}
                initial_value={facility?.prefecture}
                placeholder="選択してください"
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="text"
                label="住所"
                label_class={styles["p-edit__form-title"]}
                name="sonic_address"
                style_class={styles["p-edit__form-input"]}
                handler={handleInputChange}
                initial_value={facility?.address}
                placeholder="東京都渋谷区大山町1-1-1 セレクトビル"
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="text"
                label="MAP"
                label_class={styles["p-edit__form-title"]}
                name="sonic_map"
                style_class={styles["p-edit__form-input"]}
                handler={handleInputChange}
                initial_value={facility?.map}
                placeholder="https://goo.gl/maps/○○○○"
                merit_text="Google MapのURLを入力するとリンクが表示できます。"
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="textarea"
                label="アクセス"
                label_class={styles["p-edit__form-title"]}
                name="sonic_access"
                style_class={`${styles["p-edit__form-input"]} ${styles["resizable"]}`}
                handler={handleInputChange}
                initial_value={facility?.access}
                placeholder="代々木上原駅から徒歩5分"
                merit_text="最寄り駅や車でのアクセス時間を入力してください。"
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="textarea"
                label="営業時間"
                label_class={styles["p-edit__form-title"]}
                name="sonic_business_days"
                style_class={`${styles["p-edit__form-input"]} ${styles["resizable"]}`}
                handler={handleInputChange}
                initial_value={facility?.businessDays}
                placeholder="日〜木 10:00〜21:00 / 金・土・祝前日 10:00〜23:00"
                merit_text="開店/閉店時間と曜日ごとの営業時間の入力がおすすめです。"
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="textarea"
                label="料金"
                label_class={styles["p-edit__form-title"]}
                name="sonic_price"
                style_class={`${styles["p-edit__form-input"]} ${styles["resizable"]}`}
                handler={handleInputChange}
                initial_value={facility?.price}
                placeholder={`入会費：500円 / 年会費:0円\n1時間：400円\n2時間：700円\n6時間：2000円`}
                merit_text="入会費等がある場合もこちらに入力するのがおすすめです。"
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="textarea"
                label="座席"
                label_class={styles["p-edit__form-title"]}
                name="sonic_seats"
                style_class={`${styles["p-edit__form-input"]} ${styles["resizable"]}`}
                handler={handleInputChange}
                initial_value={facility?.seats}
                placeholder="座席総数：31席 / 完全集中ルーム(予約制)：4席"
                merit_text="座席の数や種類の入力がおすすめです。"
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="textarea"
                label="設備/スペック"
                label_class={styles["p-edit__form-title"]}
                name="sonic_machines"
                style_class={`${styles["p-edit__form-input"]} ${styles["resizable"]}`}
                handler={handleInputChange}
                initial_value={facility?.machines}
                placeholder={`PC：○○○○\nOS:○○○\nメモリ:○GB\nチェア:○○○○\nなど`}
                merit_text="PCやハードのスペック、遊べるゲームなどを入力してください。"
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="textarea"
                label="施設紹介"
                label_class={styles["p-edit__form-title"]}
                name="sonic_introduction"
                style_class={`${styles["p-edit__form-input"]} ${styles["resizable"]}`}
                handler={handleInputChange}
                initial_value={facility?.introduction}
                placeholder={`東京都渋谷区に新規オープンました！ハイスペックPCを揃えています！
遊べるゲームは30タイトル超！全席ゲーミングチェア配備で長時間の滞在も快適です！
お友達やパートナー、ご家族とも楽しめるeスポーツカフェとなっております。ご来店お待ちしています！`}
                merit_text="施設の特徴、施設からのメッセージを自由にご入力ください。"
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="radio"
                label="喫煙設定"
                label_class={styles["p-edit__form-title"]}
                name="sonic_smoking"
                handler={handleInputChange}
                initial_value={facility?.smoking ?? Smoking.None}
                merit_text="喫煙可能かを選択してください。"
                options={[
                  [Smoking.None, "設定なし"],
                  [Smoking.NotAllow, "禁煙"],
                  [Smoking.Separate, "分煙"],
                  [Smoking.Allow, "喫煙可"],
                ]}
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="radio"
                label="イベント開催"
                label_class={styles["p-edit__form-title"]}
                name="sonic_host_events"
                handler={handleInputChange}
                initial_value={facility?.hostEvents ?? "event_none"}
                merit_text="イベント開催が可能かを選択してください。"
                options={[
                  ["event_none", "設定なし"],
                  ["event_true", "OK"],
                  ["event_false", "NG"],
                ]}
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="radio"
                label="フード提供"
                label_class={styles["p-edit__form-title"]}
                name="sonic_food_service"
                handler={handleInputChange}
                initial_value={facility?.foodService ?? "food_none"}
                merit_text="飲食物の提供の有無を選択してください。"
                options={[
                  ["food_none", "設定なし"],
                  ["food_true", "あり"],
                  ["food_false", "なし"],
                ]}
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="radio"
                label="予約利用"
                label_class={styles["p-edit__form-title"]}
                name="sonic_reservation"
                handler={handleInputChange}
                initial_value={facility?.reservation ?? "reservation_none"}
                merit_text="予約での利用が可能かを選択してください。"
                options={[
                  ["reservation_none", "設定なし"],
                  ["reservation_true", "OK"],
                  ["reservation_false", "NG"],
                ]}
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <div className={styles["p-edit__form-title"]}>施設タグ</div>
              <p>当てはまるものを選択してください。</p>
              <CheckInputArea
                name="sonic_traits"
                checkOptions={facilityTraitOptions}
                baseColor="white"
                checked={traits}
                handleChange={handleTraits}
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <div className={styles["p-edit__form-title"]}>施設タイプ</div>
              <p>当てはまるものを選択してください。</p>
              <CheckInputArea
                name="sonic_facility_types"
                checkOptions={facilityTypeOptions}
                baseColor="white"
                checked={types}
                handleChange={handleTypes}
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="text"
                label="電話番号"
                label_class={styles["p-edit__form-title"]}
                name="sonic_tel"
                style_class={styles["p-edit__form-input"]}
                handler={handleInputChange}
                initial_value={facility?.tel}
                placeholder="08012345678"
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="text"
                label="公式サイトURL"
                label_class={styles["p-edit__form-title"]}
                name="sonic_official_site"
                style_class={styles["p-edit__form-input"]}
                handler={handleInputChange}
                initial_value={facility?.officialSite}
                placeholder="URLを入力"
                val_type={ValType.URL}
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="text"
                label="X (旧Twitter)"
                label_class={styles["p-edit__form-title"]}
                name="sonic_twitter"
                style_class={styles["p-edit__form-input"]}
                handler={handleInputChange}
                initial_value={facility?.sns?.twitter}
                placeholder="URLを入力"
                val_type={ValType.Twitter}
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="text"
                label="Instagram"
                label_class={styles["p-edit__form-title"]}
                name="sonic_instagram"
                style_class={styles["p-edit__form-input"]}
                handler={handleInputChange}
                initial_value={facility?.sns?.instagram}
                placeholder="URLを入力"
                val_type={ValType.Instagram}
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="text"
                label="Facebook"
                label_class={styles["p-edit__form-title"]}
                name="sonic_facebook"
                style_class={styles["p-edit__form-input"]}
                handler={handleInputChange}
                initial_value={facility?.sns?.facebook}
                placeholder="URLを入力"
                val_type={ValType.Facebook}
              />
            </div>
            <div className={`${styles["p-edit__form"]} ${styles["youtube"]}`}>
              <FormInput
                type="text"
                label="Youtube"
                label_class={styles["p-edit__form-title"]}
                name="sonic_youtube"
                style_class={styles["p-edit__form-input"]}
                handler={handleInputChange}
                initial_value={
                  facility?.sns?.youtube
                    ? facility.sns.youtube
                    : "https://youtube.com/channel/"
                }
                placeholder="https://youtube.com/channel/チャンネルID"
                val_type={ValType.Youtube}
                label_supplement={<ChannelURLHelper />}
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <div className={styles["p-edit__form-title"]}>
                施設イメージ画像
                <div className={styles["p-edit__title-desc"]}>
                  画像を設定することで施設がユーザーの目に留まりやすくなります。画像は中心が表示されます。
                </div>
              </div>
              <FormMultipleImages
                imgItems={imgItems}
                deleteHandler={deleteImgItem}
                addHandler={handleInputChange}
                addBtnClass={styles["p-edit__form-addImg"]}
                supplement={
                  <div className={styles["p-edit__form-supplement"]}>
                    <p className={styles["p-edit__form-supplement__text"]}>
                      ※１枚目の画像がサムネイルになります
                    </p>
                    <p className={styles["p-edit__form-supplement__limit"]}>
                      アップロード上限：最大5枚、トータル10MB以内
                    </p>
                  </div>
                }
              />
            </div>
          </Form>
          {error ? (
            <div className={styles["p-edit__error"]}>
              必須項目が未入力、または入力内容に誤りがあります
            </div>
          ) : null}

          {handleSave ? <SaveButton title="保存" onClick={handleSave} /> : null}

          {(handleSubmit && !isHasChanges) || !handleSave ? (
            <SaveButton title="公開申請" onClick={handleSubmit} />
          ) : null}
        </>
      ) : (
        <LoadingSpinner space={1} />
      )}
      {isHasChanges || requestState !== RequestState.Waiting ? (
        <NotificationModal
          color={genNotificationColor(requestState)}
          icon_class={genNotificationIcon(requestState)}
          message={genNotificationMessage(requestState)}
          img_src={requestState === RequestState.Success ? SelenaA : undefined}
        />
      ) : null}
    </>
  );
};

export default FacilityForm;
